import { api, unwrap, type Schemas } from '../../api/client';
import { useApiQuery } from '../../api/hooks';
import { AUTH_MODE, DEV_USER, DEV_USER_KEY, devUserKeyFor } from '../../auth/session';

// Group financial reporting (ADR 0015). Groups belong to the signed-in user, not to a business, so
// the API needs no business header; queries are still keyed under the current business like every
// other query (the shell starts fresh on a switch anyway).

export type Group = Schemas['Group'];
export type GroupReport = Schemas['GroupReport'];
export type GroupSummary = Schemas['GroupSummary'];
export type GroupCandidate = Schemas['GroupCandidate'];
export type Statement = 'profit-and-loss' | 'balance-sheet' | 'cash-flow';
export type Mode = 'entity' | 'combined' | 'consolidated';

function read(key: string): string | null {
  try { return localStorage.getItem(key); } catch { return null; }
}

/**
 * Development only: group routes act as a user, and the dev headers carry none. The dev user is
 * the business member with the dev email; its id is kept and sent as x-dev-user (session.tsx). The
 * API checks the user exists and applies every membership rule to it.
 */
export async function ensureDevUser(): Promise<void> {
  if (AUTH_MODE !== 'dev' || read(DEV_USER_KEY)) return;
  const members = (await unwrap(api.GET('/v1/members'))).data ?? [];
  const me = members.find((m) => m.email?.toLowerCase() === DEV_USER.email);
  if (me?.user_id) {
    try { localStorage.setItem(DEV_USER_KEY, me.user_id); } catch { /* storage blocked */ }
  }
}

/**
 * Development only: expenses and trips are claimed by a person, so in a business the dev headers
 * act as its dev user, or its first active owner when it has none (a business made outside the
 * app). Kept per business and sent as x-dev-user (session.tsx).
 */
export async function ensureDevUserFor(tenantId: string): Promise<void> {
  if (AUTH_MODE !== 'dev' || read(devUserKeyFor(tenantId))) return;
  const members = (await unwrap(api.GET('/v1/members'))).data ?? [];
  const me = members.find((m) => m.email?.toLowerCase() === DEV_USER.email && m.active)
    ?? members.find((m) => m.role === 'owner' && m.active);
  if (me?.user_id) {
    try { localStorage.setItem(devUserKeyFor(tenantId), me.user_id); } catch { /* storage blocked */ }
  }
}

export const useGroups = () =>
  useApiQuery(['groups', 'list'], async () => {
    await ensureDevUser();
    return (await unwrap(api.GET('/v1/groups'))).data as { groups: GroupSummary[]; companies: GroupCandidate[] };
  });

export const useGroup = (id: string | undefined) =>
  useApiQuery(['groups', 'detail', id], async () => {
    await ensureDevUser();
    return (await unwrap(api.GET('/v1/groups/{id}', { params: { path: { id: id as string } } }))).data as Group;
  }, { enabled: !!id });

export interface ReportParams {
  statement: Statement;
  mode: Mode;
  period: string;
  entities: string[];
  entity: string | null;
}

export const useGroupReport = (id: string | undefined, p: ReportParams, enabled: boolean) =>
  useApiQuery(['groups', 'report', id, p], async () => {
    await ensureDevUser();
    const query: { mode: Mode; period: string; entities?: string; entity?: string } = { mode: p.mode, period: p.period };
    if (p.mode === 'entity' && p.entity) query.entity = p.entity;
    if (p.mode !== 'entity') query.entities = p.entities.join(',');
    return (await unwrap(api.GET('/v1/groups/{id}/reports/{statement}', { params: { path: { id: id as string, statement: p.statement }, query } }))).data as GroupReport;
  }, { enabled: enabled && !!id, retry: false });

export { defaultPeriod, monthName, periodOptions } from './period';

export const STATEMENTS: [Statement, string][] = [['profit-and-loss', 'Profit and loss'], ['balance-sheet', 'Balance sheet'], ['cash-flow', 'Cash flow']];
export const MODES: [Mode, string, string][] = [
  ['entity', 'Single company', 'One company’s financials: the P&L, balance sheet and cash flow for one company, in its own currency.'],
  ['combined', 'Combined', 'Selected companies added together, in the group currency. Transactions between them are not eliminated.'],
  ['consolidated', 'Consolidated', 'Group accounts with intercompany eliminations, currency translation and consolidation adjustments.'],
];

/** Common presentation currencies first; any 3-letter code is accepted. */
export const CURRENCIES = ['USD', 'EUR', 'GBP', 'ZAR', 'CAD', 'AUD', 'NZD', 'CHF', 'JPY', 'CNY', 'INR', 'SGD', 'AED', 'KES', 'NGN', 'BWP', 'NAD', 'MZN'];
